import type { WorkflowCatalogEntry } from "./catalog";
export const TASK_QUEUE = "gtm.workflows";
/**
 * Input every catalog workflow starts with. `sourceId` is what the workflow
 * runs against: a collection source, or a delivery connection. The Temporal
 * workflow ID is the run's request key: a schedule fires
 * `<scheduleId>-<scheduled time>` and a manual trigger starts
 * `<prefix>-<requested time>`, so one workflow ID maps to exactly one run.
 */
export interface RunRequest {
  tenantId: string;
  sourceId: string;
  workflowKey: WorkflowCatalogEntry["key"];
  trigger: "manual" | "schedule";
  userId?: string;
}
export type CollectionRequest = RunRequest;
/** A delivery run carries the records it sends; the connection is its source. */
export interface DeliveryRequest extends RunRequest {
  keys: string[];
  signalKeys?: string[];
}
export interface CollectionJob {
  tenantId: string;
  runId: string;
}
export interface DeliveryJob {
  tenantId: string;
  deliveryId: string;
}
export interface HydrationRequest {
  tenantId: string;
  integrationId: string;
  identityIds: string[];
  offset?: number;
}
export const collectionWorkflowTypes = {
  "reddit.sync_subreddit": "redditSubredditSync",
  "github.sync_repository": "githubRepositorySync",
  "github.sync_stargazers": "githubStargazersSync",
  "github.sync_issues": "githubIssuesSync",
  "github.sync_activity": "githubCommunitySync",
} as const;
export const deliveryWorkflowTypes = {
  "slack.send_records": "slackSendRecords",
  "webhook.send_records": "webhookSendRecords",
} as const;
/** Every catalog workflow type; what the Runs page lists. */
export const workflowTypes = {
  ...collectionWorkflowTypes,
  ...deliveryWorkflowTypes,
} as const;

/**
 * The schedule ID for a source/workflow pair, and the prefix of every workflow
 * ID that pair produces: Temporal appends the scheduled time when a schedule
 * fires, and a manual trigger appends the requested time.
 */
export const collectionScheduleId = (tenantId: string, sourceId: string, workflowKey: string) =>
  `collection/${tenantId}/${sourceId}/${workflowKey}`;

/** The workflow ID prefix a manual trigger extends with the requested time. */
export const runWorkflowIdPrefix = (tenantId: string, sourceId: string, workflowKey: string) =>
  workflowKey in deliveryWorkflowTypes
    ? `delivery/${tenantId}/${sourceId}/${workflowKey}`
    : collectionScheduleId(tenantId, sourceId, workflowKey);

/** Every attempt at one delivery row runs under this prefix. */
export const deliveryWorkflowIdPrefix = (tenantId: string, deliveryId: string) =>
  `delivery/${tenantId}/${deliveryId}`;

export interface WorkflowSchedule {
  id: string;
  sourceId: string;
  sourceName: string;
  workflowKey: WorkflowCatalogEntry["key"];
  paused: boolean;
  cron: string;
  nextRun: string | null;
}

export interface WorkflowInputs {
  redditSubredditSync: CollectionRequest;
  redditScheduledSync: Omit<CollectionRequest, "trigger">;
  githubRepositorySync: CollectionRequest;
  githubStargazersSync: CollectionRequest;
  githubIssuesSync: CollectionRequest;
  githubCommunitySync: CollectionRequest;
  githubScheduledSync: Omit<CollectionRequest, "trigger">;
  githubProfilesEnrich: HydrationRequest;
  githubProfilesEnrichShard: HydrationRequest;
  slackSendRecords: DeliveryRequest;
  webhookSendRecords: DeliveryRequest;
  deliveryWorkflow: DeliveryJob;
}
